import React from "react";
import { ArrowUpRight, CheckCircle, ChevronRight, Clock, DollarSign, Percent, ShieldAlert, Target, Users, Wallet } from "lucide-react";
import { inr } from "@/lib/format";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";

export default function ExecutivePulseCards({
  waterfall,
  sales,
  targets,
  labourPulse,
  menuEngineering,
  outflow,
  onOpenTargetModal,
}) {
  const bookedSales = waterfall?.booked_invoiced_sales ?? (sales?.total_sales || 0);
  const liquidCollections = waterfall?.liquid_collections ?? (sales?.net_sales || 0);
  const targetAmt = targets?.target_amount || 0;
  const progressPct = targets?.progress_pct ?? (targetAmt > 0 ? Math.round((bookedSales / targetAmt) * 100) : 0);

  // Coverage badge styling
  const coverageBadge = menuEngineering?.data_quality?.coverage_badge || "Uncosted";
  const getCoverageStyle = (badge) => {
    switch (badge) {
      case "Full Coverage":
        return "bg-success-subtle text-success border-success/30";
      case "Partial Coverage":
        return "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const netCashFlow = liquidCollections - (outflow?.total || 0);

  return (
    <div className="grid gap-base sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="dash-executive-pulse">
      {/* 1. Booked Invoiced Sales */}
      <div className="flex flex-col justify-between rounded-lg border bg-card p-base">
        <div>
          <div className="flex items-center justify-between text-meta font-medium text-muted-foreground">
            <span>Booked Invoiced Sales</span>
            <button
              onClick={onOpenTargetModal}
              className="inline-flex items-center gap-hair rounded px-tight py-hair text-[11px] font-medium text-primary hover:bg-primary/10 transition-colors"
              title="Configure Sales Targets"
            >
              <Target className="h-3 w-3" />
              <span>Target</span>
            </button>
          </div>
          <div className="mt-tight text-title font-bold tabular-nums" data-testid="kpi-booked-sales">
            {inr(bookedSales)}
          </div>
        </div>

        <div className="mt-base space-y-hair">
          <div className="flex justify-between text-[11px] text-muted-foreground">
            <span>Target: {targetAmt > 0 ? inr(targetAmt) : "None set"}</span>
            <span className="font-semibold tabular-nums">{progressPct}%</span>
          </div>
          <Progress value={Math.min(progressPct, 100)} className="h-1.5" />
        </div>
      </div>

      {/* 2. Settled Liquid Collections */}
      <div className="flex flex-col justify-between rounded-lg border bg-card p-base">
        <div>
          <div className="flex items-center justify-between text-meta font-medium text-muted-foreground">
            <span>Liquid Collections</span>
            <span className="text-[10px] rounded bg-success-subtle px-tight py-hair font-semibold text-success">
              Settled
            </span>
          </div>
          <div className="mt-tight text-title font-bold tabular-nums text-foreground" data-testid="kpi-liquid-collections">
            {inr(liquidCollections)}
          </div>
        </div>

        <div className="mt-base border-t pt-tight text-[11px] text-muted-foreground flex justify-between">
          <span>Cash: {inr(waterfall?.tender_breakdown?.cash || 0)}</span>
          <span>UPI/Bank: {inr((waterfall?.tender_breakdown?.upi || 0) + (waterfall?.tender_breakdown?.card || 0))}</span>
        </div>
      </div>

      {/* 3. Gross Margin & Recipe Coverage */}
      <div className="flex flex-col justify-between rounded-lg border bg-card p-base">
        <div>
          <div className="flex items-center justify-between text-meta font-medium text-muted-foreground">
            <span>Gross Margin</span>
            <span className={`text-[10px] rounded px-tight py-hair font-semibold border ${getCoverageStyle(coverageBadge)}`}>
              {coverageBadge}
            </span>
          </div>
          <div className="mt-tight flex items-baseline gap-snug">
            <span className="text-title font-bold tabular-nums text-foreground">
              {menuEngineering?.overall_margin_pct ?? 0}%
            </span>
            <span className="text-meta text-muted-foreground tabular-nums">
              Food Cost: {menuEngineering?.food_cost_pct ?? 0}%
            </span>
          </div>
        </div>

        <div className="mt-base border-t pt-tight text-[11px] text-muted-foreground">
          <span>{menuEngineering?.data_quality?.costed_items_count || 0} of {menuEngineering?.data_quality?.total_items_count || 0} active items costed</span>
        </div>
      </div>

      {/* 4. Labour Productivity Pulse */}
      <div className="flex flex-col justify-between rounded-lg border bg-card p-base">
        <div>
          <div className="flex items-center justify-between text-meta font-medium text-muted-foreground">
            <span>Labour Pulse</span>
            <span className="inline-flex items-center gap-hair text-[11px] text-muted-foreground">
              <Users className="h-3 w-3" />
              <span>{labourPulse?.active_staff_count || 0} on duty</span>
            </span>
          </div>
          <div className="mt-tight text-title font-bold tabular-nums" data-testid="kpi-sales-per-hour">
            {inr(labourPulse?.sales_per_labour_hour || 0)}
            <span className="text-meta font-normal text-muted-foreground ml-tight">/ hr</span>
          </div>
        </div>

        <div className="mt-base border-t pt-tight text-[11px] text-muted-foreground flex justify-between">
          <span>Hours: {labourPulse?.total_labour_hours || 0}h</span>
          {labourPulse?.overtime_hours > 0 ? (
            <span className="text-amber-600 dark:text-amber-400 font-medium">OT: {labourPulse.overtime_hours}h</span>
          ) : (
            <span>No Overtime</span>
          )}
        </div>
      </div>

      {/* 5. Net Cashflow (Takings vs Drain) */}
      <div className="flex flex-col justify-between rounded-lg border bg-card p-base">
        <div>
          <div className="flex items-center justify-between text-meta font-medium text-muted-foreground">
            <span>Net Liquid Flow</span>
            <span className="text-[11px] text-muted-foreground">Takings - Outflow</span>
          </div>
          <div className={`mt-tight text-title font-bold tabular-nums ${netCashFlow >= 0 ? "text-success" : "text-danger"}`} data-testid="kpi-net-flow">
            {inr(netCashFlow)}
          </div>
        </div>

        <div className="mt-base border-t pt-tight text-[11px] text-muted-foreground flex justify-between">
          <span>Expenses: {inr(outflow?.expenses || 0)}</span>
          <span>Outflow: {inr(outflow?.total || 0)}</span>
        </div>
      </div>
    </div>
  );
}
